import { useEffect } from 'react';

/**
 * Name the browser tab after what is actually on the page.
 *
 * Every route here shipped as the literal word "Event", because one static
 * `<title>` in index.html serves the whole SPA and nothing ever replaced it.
 * A person with several tabs open — the form an organiser sent them, the event
 * site, their own inbox — had no way to tell which was which, and a bookmark
 * or a shared-tab screenshot recorded a page that could have been anybody's.
 *
 * Pass `null` while the data that names the page has not arrived. The static
 * fallback then stands rather than the tab flickering through a placeholder,
 * which is the one thing worse than a generic title.
 *
 * The previous title is restored on unmount so a route that names itself
 * cannot leave its name on the route the person navigates to next.
 */
export function useDocumentTitle(title: string | null): void {
  useEffect(() => {
    if (title === null) return;

    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * Join a page name to the event it belongs to.
 *
 * Event name last, so the specific part survives the truncation a narrow tab
 * applies from the right — "Dietary requirements" is what tells two open tabs
 * apart, and the event name is the part the reader can already infer.
 */
export const titleFor = (page: string | null, eventName: string): string =>
  page && page !== eventName ? `${page} · ${eventName}` : eventName;
